// OutageDetails.tsx
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, User, MapPin, Zap } from 'lucide-react';
import {
  getOutageRequisitionDetail,
  notifySrConfirm,
  getSeniorPlanners,
  markSrConfirmed,
  getTechnicalSupportOfficers,
  notifyTSOConfirm,
  markTSOConfirmed,
  getMaintenanceManagers,
  notifyMMApprove,
  markMMApproved,
  getTechnicalManagers,
  notifyTMApprove,
  markTMApproved,
  getOperationManagers,
  notifyOMApprove,
  markOMApproved,
  getShiftSupervisors,
  notifyShutdown,
  markShutdown,
  downloadOutageRequisitionPdf,
} from '../../../services/outageRequisitionSevice';
import Loader from '../../../components/Loader/Loader';
import { useProfile } from '../../../Contexts/ProfileContext';
import RejectButton from '../../../components/RejectButton/RejectButton';
import ErrorModal from '../../../components/ConfirmModal/ErrorModal';

type OutageReason = {
  title: string;
  description: string;
};

type OutageDetailsType = {
  id: string;
  referenceNo: string;
  createdBy: string;
  status: string | null;
  requestDate: string | null;
  requester: string;
  site: string;
  unit: string | null;
  outageStartDate: string | null;
  outageEndDate: string | null;
  startTime: string | null;
  endTime: string | null;
  outageDuration: string | null;
  isolations: string | null;
  systemConstraints: string | null;
  previousOutageRemarks: string | null;
  outageType: string | null;
  reasons: OutageReason[];
};

type SearchableSelectProps = {
  options: { id: number | string; name: string }[];
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
  disabled?: boolean;
};

const SearchableSelect: React.FC<SearchableSelectProps> = ({ options, value, onChange, placeholder = 'Search...', disabled }) => {
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  // Keep the input text in sync with the selected id (e.g. on modal reopen)
  useEffect(() => {
    const selected = options.find((o) => String(o.id) === value);
    setSearch(selected ? selected.name : '');
  }, [value, options]);

  const filtered = options.filter((o) => o.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="relative">
      <input
        type="text"
        placeholder={placeholder}
        value={search}
        disabled={disabled}
        onChange={(e) => {
          setSearch(e.target.value);
          onChange('');
          setShowDropdown(true);
        }}
        onFocus={() => setShowDropdown(true)}
        onBlur={() =>
          setTimeout(() => {
            setShowDropdown(false);
            const match = options.find((o) => o.name.toLowerCase() === search.toLowerCase());
            if (!match) {
              setSearch('');
              onChange('');
            }
          }, 200)
        }
        className="w-full border rounded-md px-3 py-2 disabled:opacity-50 disabled:bg-gray-100"
      />

      {showDropdown && !disabled && (
        <div className="absolute z-20 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto mt-1">
          {filtered.length === 0 ? (
            <div className="px-4 py-2 text-sm text-gray-500">No results found</div>
          ) : (
            filtered.map((option) => (
              <div
                key={option.id}
                onMouseDown={() => {
                  onChange(String(option.id));
                  setSearch(option.name);
                  setShowDropdown(false);
                }}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
              >
                {option.name}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const OutageReqDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const profile = useProfile();
  const [data, setData] = useState<OutageDetailsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [isShutdownModalOpen, setIsShutdownModalOpen] = useState(false);

  const [seniorPlanners, setSeniorPlanners] = useState<any[]>([]);
  const [selectedPlanner, setSelectedPlanner] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [notified, setNotified] = useState(false);

  const [showConfirmOutageModal, setShowConfirmOutageModal] = useState(false);
  const [outageConfirmed, setOutageConfirmed] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const [currentUserId, setCurrentUserId] = useState('unknown_user');
  const [pendingApproverId, setPendingApproverId] = useState<number | null>(null);

  const [showTSOModal, setShowTSOModal] = useState(false);
  const [technicalSupportOfficers, setTechnicalSupportOfficers] = useState<any[]>([]);
  const [selectedTSO, setSelectedTSO] = useState('');
  const [tsoNotified, setTsoNotified] = useState(false);

  const [showTSOConfirmModal, setShowTSOConfirmModal] = useState(false);
  const [tsoConfirmLoading, setTsoConfirmLoading] = useState(false);
  const [tsoConfirmed, setTsoConfirmed] = useState(false);

  const [showMMModal, setShowMMModal] = useState(false);
  const [maintenanceManagers, setMaintenanceManagers] = useState<any[]>([]);
  const [selectedMM, setSelectedMM] = useState('');
  const [mmNotified, setMmNotified] = useState(false);

  const [showMMApproveModal, setShowMMApproveModal] = useState(false);
  const [mmApproveLoading, setMmApproveLoading] = useState(false);
  const [mmApproved, setMmApproved] = useState(false);
  const [mmRemarks, setMmRemarks] = useState('');

  const [showTMModal, setShowTMModal] = useState(false);
  const [technicalManagers, setTechnicalManagers] = useState<any[]>([]);
  const [selectedTM, setSelectedTM] = useState('');
  const [tmNotified, setTmNotified] = useState(false);

  const [showTMApproveModal, setShowTMApproveModal] = useState(false);
  const [tmApproveLoading, setTmApproveLoading] = useState(false);
  const [tmApproved, setTmApproved] = useState(false);
  const [tmRemarks, setTmRemarks] = useState('');

  const [showOMModal, setShowOMModal] = useState(false);
  const [operationManagers, setOperationManagers] = useState<any[]>([]);
  const [selectedOM, setSelectedOM] = useState('');
  const [omNotified, setOmNotified] = useState(false);

  const [showOMApproveModal, setShowOMApproveModal] = useState(false);
  const [omApproveLoading, setOmApproveLoading] = useState(false);
  const [omApproved, setOmApproved] = useState(false);
  const [omRemarks, setOmRemarks] = useState('');

  const [showShiftSupervisorModal, setShowShiftSupervisorModal] = useState(false);
  const [shiftSupervisors, setShiftSupervisors] = useState<any[]>([]);
  const [selectedShiftSupervisor, setSelectedShiftSupervisor] = useState('');
  const [shiftSupervisorNotified, setShiftSupervisorNotified] = useState(false);

  const [showShutdownConfirmModal, setShowShutdownConfirmModal] = useState(false);
  const [shutdownLoading, setShutdownLoading] = useState(false);
  const [shutdownConfirmed, setShutdownConfirmed] = useState(false);

  const [confirmSrIds, setConfirmSrIds] = useState<string[]>([]);
  const [confirmTsoIds, setConfirmTsoIds] = useState<string[]>([]);
  const [confirmMMIds, setConfirmMMIds] = useState<string[]>([]);
  const [confirmTMIds, setConfirmTMIds] = useState<string[]>([]);
  const [confirmOMIds, setConfirmOMIds] = useState<string[]>([]);
  const [confirmSSIds, setConfirmSSIds] = useState<string[]>([]);

  const [notifyLoading, setNotifyLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const [errorModal, setErrorModal] = useState(false);

  const [errorMessage, setErrorMessage] = useState({
    title: '',
    message: '',
  });

  useEffect(() => {
    setCurrentUserId(localStorage.getItem('currentUserID') || '{}');
  }, []);

  const fetchData = async () => {
    try {
      const res = await getOutageRequisitionDetail(id!);
      const outage = res.data;
      const getPendingApproverIds = (workflowStages: any[], stageName: string) =>
        (Array.isArray(workflowStages) ? workflowStages : [])
          .filter((stage: any) => stage?.stage === stageName)
          .flatMap((stage: any) =>
            (Array.isArray(stage?.approvers) ? stage.approvers : [])
              .filter((approver: any) => approver?.status === 'PENDING')
              .map((approver: any) => (approver?.user?.id ?? approver?.user_id ?? approver?.id)?.toString())
              .filter(Boolean),
          );

      setConfirmSrIds(getPendingApproverIds(outage.workflow_stages, 'SR_CONFIRM'));
      setConfirmTsoIds(getPendingApproverIds(outage.workflow_stages, 'TSO_CONFIRM'));
      setConfirmMMIds(getPendingApproverIds(outage.workflow_stages, 'MM_APPROVE'));
      setConfirmTMIds(getPendingApproverIds(outage.workflow_stages, 'TM_APPROVE'));
      setConfirmOMIds(getPendingApproverIds(outage.workflow_stages, 'OM_APPROVE'));
      setConfirmSSIds(getPendingApproverIds(outage.workflow_stages, 'SHUTDOWN'));
      //        const pendingStage = outage.workflow_stages?.find(
      //   (stage: any) => stage.status === 'PENDING'
      // );

      // const approverId = pendingStage?.approvers?.[0]?.user?.id;

      // setPendingApproverId(approverId || null);

      const mappedData: OutageDetailsType = {
        id: `#OUR${outage.id}`,
        referenceNo: outage.reference_number,
        createdBy: outage.created_by?.id || outage.created_by || 'N/A',
        status: outage.status || 'Pending',
        requestDate: outage.date_of_requisition || outage.created_at,
        requester: outage.user?.name || 'N/A',
        site: outage.site?.name || 'N/A',
        unit:
          typeof outage.unit === 'object' && outage.unit !== null ? outage.unit.title || outage.unit.name || 'N/A' : outage.unit || 'N/A',
        outageStartDate: outage.outage_start_date || null,
        outageEndDate: outage.outage_end_date || null,
        startTime: outage.start_time || null,
        endTime: outage.end_time || null,
        outageDuration: outage.outage_duration || null,
        isolations: outage.isolations || null,
        systemConstraints: outage.system_constraints || null,
        previousOutageRemarks: outage.previous_outage_remarks || null,
        outageType:
          typeof outage.outage_type === 'object' && outage.outage_type !== null ? outage.outage_type.title : outage.outage_type || 'N/A',
        reasons: outage.reason_for_outage ? [{ title: 'Reason', description: outage.reason_for_outage }] : [],
      };

      setData(mappedData);
      if (
        outage.status === 'Outage Type Confirmation Pending - Senior Planner' ||
        outage.status === 'Outage Type Confirmed - Senior Planner'
      ) {
        setNotified(true);
      }

      if (outage.status === 'Outage Type Confirmed') {
        setOutageConfirmed(true);
      }

      if (outage.status === 'Outage Type Confirmation Pending - Technical Support Officer') {
        setNotified(true);
        setOutageConfirmed(true);
        setTsoNotified(true);
      }

      if (outage.status === 'Outage Type Confirmed - Technical Support Officer') {
        setNotified(true);
        setOutageConfirmed(true);
        setTsoNotified(true);
        setTsoConfirmed(true);
      }

      if (outage.status === 'Approval Pending - Maintenance Manager') {
        setNotified(true);
        setOutageConfirmed(true);
        setTsoNotified(true);
        setTsoConfirmed(true);
        setMmNotified(true);
      }

      if (outage.status === 'Approved - Maintenance Manager') {
        setNotified(true);
        setOutageConfirmed(true);
        setTsoNotified(true);
        setTsoConfirmed(true);
        setMmNotified(true);
        setMmApproved(true);
      }

      if (outage.status === 'Approval Pending - Technical Manager') {
        setNotified(true);
        setOutageConfirmed(true);
        setTsoNotified(true);
        setTsoConfirmed(true);
        setMmNotified(true);
        setMmApproved(true);
        setTmNotified(true);
      }

      if (outage.status === 'Approved - Technical Manager') {
        setNotified(true);
        setOutageConfirmed(true);
        setTsoNotified(true);
        setTsoConfirmed(true);
        setMmNotified(true);
        setMmApproved(true);
        setTmNotified(true);
        setTmApproved(true);
      }

      if (outage.status === 'Approval Pending - Operation Manager') {
        setNotified(true);
        setOutageConfirmed(true);
        setTsoNotified(true);
        setTsoConfirmed(true);
        setMmNotified(true);
        setMmApproved(true);
        setTmNotified(true);
        setTmApproved(true);
        setOmNotified(true);
      }

      if (outage.status === 'Approved - Operation Manager') {
        setNotified(true);
        setOutageConfirmed(true);
        setTsoNotified(true);
        setTsoConfirmed(true);
        setMmNotified(true);
        setMmApproved(true);
        setTmNotified(true);
        setTmApproved(true);
        setOmNotified(true);
        setOmApproved(true);
      }

      if (outage.status === 'Shutdown Pending') {
        setNotified(true);
        setOutageConfirmed(true);
        setTsoNotified(true);
        setTsoConfirmed(true);
        setMmNotified(true);
        setMmApproved(true);
        setTmNotified(true);
        setTmApproved(true);
        setOmNotified(true);
        setOmApproved(true);
        setShiftSupervisorNotified(true);
      }

      if (outage.status === 'Shutdown Confirmed') {
        setNotified(true);
        setOutageConfirmed(true);
        setTsoNotified(true);
        setTsoConfirmed(true);
        setMmNotified(true);
        setMmApproved(true);
        setTmNotified(true);
        setTmApproved(true);
        setOmNotified(true);
        setOmApproved(true);
        setShiftSupervisorNotified(true);
        setShutdownConfirmed(true);
      }
    } catch (err) {
      console.error('Failed to fetch outage detail:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleShutdownClick = async () => {
    setIsShutdownModalOpen(true);

    try {
      const res = await getSeniorPlanners();

      const users = (res?.data ?? res ?? []).map((u: any) => ({
        id: u.id,
        name: u.name || u.full_name,
      }));

      setSeniorPlanners(users);
    } catch (error) {
      console.error('Failed to fetch senior planners:', error);
    }
  };

  const handleNotifyTSOClick = async () => {
    setShowTSOModal(true);

    try {
      const res = await getTechnicalSupportOfficers();

      const users = (res?.data ?? res ?? []).map((u: any) => ({
        id: u.id,
        name: u.name || u.full_name,
      }));

      setTechnicalSupportOfficers(users);
    } catch (error) {
      console.error('Failed to fetch TSO list:', error);
    }
  };

  const handleNotifyMMClick = async () => {
    setShowMMModal(true);

    try {
      const res = await getMaintenanceManagers();

      const users = (res?.data ?? res ?? []).map((u: any) => ({
        id: u.id,
        name: u.name || u.full_name,
      }));

      setMaintenanceManagers(users);
    } catch (error) {
      console.error('Failed to fetch Maintenance Managers:', error);
    }
  };

  const handleNotifyOMClick = async () => {
    setShowTMModal(true);

    try {
      const res = await getTechnicalManagers();

      const users = (res?.data ?? res ?? []).map((u: any) => ({
        id: u.id,
        name: u.name || u.full_name,
      }));

      setTechnicalManagers(users);
    } catch (error) {
      console.error('Failed to fetch Technical Managers:', error);
    }
  };

  const handleNotifyOperationManagerClick = async () => {
    setShowOMModal(true);

    try {
      const res = await getOperationManagers();

      const users = (res?.data ?? res ?? []).map((u: any) => ({
        id: u.id,
        name: u.name || u.full_name,
      }));

      setOperationManagers(users);
    } catch (error) {
      console.error('Failed to fetch Operation Managers:', error);
    }
  };

  const handleNotifyShiftSupervisorClick = async () => {
    setShowShiftSupervisorModal(true);

    try {
      const res = await getShiftSupervisors();

      const users = (res?.data ?? res ?? []).map((u: any) => ({
        id: u.id,
        name: u.name || u.full_name,
      }));

      setShiftSupervisors(users);
    } catch (error) {
      console.error('Failed to fetch Shift Supervisors:', error);
    }
  };

  const handleShutdownConfirm = async () => {
    if (!selectedPlanner) {
      alert('Please select a Senior Planner');
      return;
    }

    try {
      setNotifyLoading(true);

      await notifySrConfirm(id!, {
        user_ids: [Number(selectedPlanner)],
      });

      setNotified(true);
      setIsShutdownModalOpen(false);
      setSuccessMessage('Notification successfully sent to the Senior Planner.');
    } catch (error: any) {
      console.error('Notify failed:', error);

      const response = error?.response?.data;

      let message = 'Something went wrong.';

      if (response?.errors) {
        const firstKey = Object.keys(response.errors)[0];

        if (firstKey) {
          message = response.errors[firstKey][0];
        }
      } else if (response?.message) {
        message = response.message;
      }

      setErrorMessage({
        title: 'Notification Failed',
        message,
      });

      setErrorModal(true);
    } finally {
      setNotifyLoading(false);
    }
  };

  const handleTSOConfirm = async () => {
    try {
      if (!selectedTSO) {
        alert('Please select a Technical Support Officer');
        return;
      }

      setNotifyLoading(true);

      await notifyTSOConfirm(id!, {
        user_ids: [Number(selectedTSO)],
      });

      setTsoNotified(true);

      setData((prev) =>
        prev
          ? {
              ...prev,
              status: 'TSO Confirmation Pending',
            }
          : prev,
      );

      setShowTSOModal(false);
      setSelectedTSO('');

      setSuccessMessage('Notification successfully sent to the Technical Support Officer to confirm the outage type.');
    } catch (error: any) {
      console.error('TSO notification failed:', error);

      const response = error?.response?.data;

      let message = 'Something went wrong.';

      if (response?.errors) {
        const firstKey = Object.keys(response.errors)[0];

        if (firstKey && response.errors[firstKey]?.length) {
          message = response.errors[firstKey][0];
        }
      } else if (response?.message) {
        message = response.message;
      }

      setErrorMessage({
        title: 'Notification Failed',
        message,
      });

      setErrorModal(true);
    } finally {
      setNotifyLoading(false);
    }
  };

  const handleMMConfirm = async () => {
    try {
      if (!selectedMM) {
        alert('Please select a Maintenance Manager');
        return;
      }
      setNotifyLoading(true);
      await notifyMMApprove(id!, {
        user_ids: [Number(selectedMM)],
      });

      setMmNotified(true);

      setData((prev) =>
        prev
          ? {
              ...prev,
              status: 'Maintenance Manager Approval Pending',
            }
          : prev,
      );

      setShowMMModal(false);
      setSelectedMM('');

      setSuccessMessage('Notification successfully sent to the Maintenance Manager to approve the outage request.');
    } catch (error: any) {
      console.error('Maintenance Manager notification failed:', error);

      const response = error?.response?.data;

      let message = 'Failed to notify Maintenance Manager.';

      if (response?.errors) {
        const firstKey = Object.keys(response.errors)[0];

        if (firstKey && response.errors[firstKey]?.length) {
          message = response.errors[firstKey][0];
        }
      } else if (response?.message) {
        message = response.message;
      }

      setErrorMessage({
        title: 'Notification Failed',
        message,
      });

      setErrorModal(true);
    }
  };

  const handleOMConfirm = async () => {
    try {
      if (!selectedTM) {
        alert('Please select an Operation Manager');
        return;
      }
      setNotifyLoading(true);
      await notifyTMApprove(id!, {
        user_ids: [Number(selectedTM)],
      });

      setTmNotified(true);

      setData((prev) =>
        prev
          ? {
              ...prev,
              status: 'Operation Manager Approval Pending',
            }
          : prev,
      );

      setShowTMModal(false);
      setSelectedTM('');

      setSuccessMessage('Notification successfully sent to the Technical Manager to approve the outage request.');
    } catch (error: any) {
      console.error('Technical Manager notification failed:', error);

      const response = error?.response?.data;

      let message = 'Failed to notify Technical Manager.';

      if (response?.errors) {
        const firstKey = Object.keys(response.errors)[0];

        if (firstKey && response.errors[firstKey]?.length) {
          message = response.errors[firstKey][0];
        }
      } else if (response?.message) {
        message = response.message;
      }

      setErrorMessage({
        title: 'Notification Failed',
        message,
      });

      setErrorModal(true);
    }
  };

  const handleOperationManagerConfirm = async () => {
    try {
      if (!selectedOM) {
        alert('Please select an Operation Manager');
        return;
      }
      setNotifyLoading(true);
      await notifyOMApprove(id!, {
        user_ids: [Number(selectedOM)],
      });

      setOmNotified(true);

      setData((prev) =>
        prev
          ? {
              ...prev,
              status: 'Operation Manager Approval Pending',
            }
          : prev,
      );

      setShowOMModal(false);
      setSelectedOM('');

      setSuccessMessage('Notification successfully sent to the Operation Manager to approve the outage request.');
    } catch (error: any) {
      console.error('Operation Manager notification failed:', error);

      const response = error?.response?.data;

      let message = 'Failed to notify Operation Manager.';

      if (response?.errors) {
        const firstKey = Object.keys(response.errors)[0];

        if (firstKey && response.errors[firstKey]?.length) {
          message = response.errors[firstKey][0];
        }
      } else if (response?.message) {
        message = response.message;
      }

      setErrorMessage({
        title: 'Notification Failed',
        message,
      });

      setErrorModal(true);
    }
  };

  const handleShiftSupervisorConfirm = async () => {
    try {
      if (!selectedShiftSupervisor) {
        alert('Please select a Shift Supervisor');
        return;
      }
      setNotifyLoading(true);
      await notifyShutdown(id!, {
        user_ids: [Number(selectedShiftSupervisor)],
      });

      setShiftSupervisorNotified(true);

      setShowShiftSupervisorModal(false);
      setSelectedShiftSupervisor('');

      setSuccessMessage('Notification successfully sent to the Shift Supervisor for the shutdown.');
    } catch (error: any) {
      console.error('Shift Supervisor notification failed:', error);

      const response = error?.response?.data;

      let message = 'Failed to notify Shift Supervisor.';

      if (response?.errors) {
        const firstKey = Object.keys(response.errors)[0];

        if (firstKey && response.errors[firstKey]?.length) {
          message = response.errors[firstKey][0];
        }
      } else if (response?.message) {
        message = response.message;
      }

      setErrorMessage({
        title: 'Notification Failed',
        message,
      });

      setErrorModal(true);
    }
  };

  const handleConfirmOutageType = async () => {
    try {
      setConfirmLoading(true);

      await markSrConfirmed(id!);

      setOutageConfirmed(true);

      setData((prev) =>
        prev
          ? {
              ...prev,
              status: 'Outage Type Confirmed',
            }
          : prev,
      );

      setShowConfirmOutageModal(false);

      setSuccessMessage('Outage Requisition confirmed successfully.');
    } catch (error: any) {
      console.error('Failed to confirm outage type:', error);

      const response = error?.response?.data;

      let message = 'Failed to confirm outage type.';

      if (response?.errors) {
        const firstKey = Object.keys(response.errors)[0];

        if (firstKey && response.errors[firstKey]?.length) {
          message = response.errors[firstKey][0];
        }
      } else if (response?.message) {
        message = response.message;
      }

      setShowConfirmOutageModal(false);

      setErrorMessage({
        title: 'Confirmation Failed',
        message,
      });

      setErrorModal(true);
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleTSOOutageConfirm = async () => {
    try {
      setTsoConfirmLoading(true);

      await markTSOConfirmed(id!);

      setTsoConfirmed(true);

      setData((prev) =>
        prev
          ? {
              ...prev,
              status: 'Outage Type Confirmed - Technical Support Officer',
            }
          : prev,
      );

      setShowTSOConfirmModal(false);

      setSuccessMessage('Outage Requisition confirmed successfully.');
    } catch (error: any) {
      console.error('Failed to confirm outage type by TSO:', error);

      const response = error?.response?.data;

      let message = 'Failed to confirm outage type.';

      if (response?.errors) {
        const firstKey = Object.keys(response.errors)[0];

        if (firstKey && response.errors[firstKey]?.length) {
          message = response.errors[firstKey][0];
        }
      } else if (response?.message) {
        message = response.message;
      }

      setShowTSOConfirmModal(false);

      setErrorMessage({
        title: 'Confirmation Failed',
        message,
      });

      setErrorModal(true);
    }
  };

  const handleMMApprove = async () => {
    try {
      setMmApproveLoading(true);

      await markMMApproved(id!, mmRemarks);

      setMmApproved(true);

      setData((prev) =>
        prev
          ? {
              ...prev,
              status: 'Approved - Maintenance Manager',
            }
          : prev,
      );

      setShowMMApproveModal(false);
      setMmRemarks(''); // reset for next time

      setSuccessMessage('Outage Requisition approved successfully.');
    } catch (error) {
      console.error('Failed to approve outage request:', error);
      alert('Failed to approve outage request');
    } finally {
      setMmApproveLoading(false);
    }
  };

  const handleTMApprove = async () => {
    try {
      setTmApproveLoading(true);

      await markTMApproved(id!, tmRemarks);

      setTmApproved(true);

      setData((prev) =>
        prev
          ? {
              ...prev,
              status: 'Approved - Technical Manager',
            }
          : prev,
      );

      setShowTMApproveModal(false);
      setTmRemarks('');

      setSuccessMessage('Outage Requisition approved successfully.');
    } catch (error) {
      console.error('Failed to approve outage request:', error);
      alert('Failed to approve outage request');
    } finally {
      setTmApproveLoading(false);
    }
  };

  const handleOMApprove = async () => {
    try {
      setOmApproveLoading(true);

      await markOMApproved(id!, omRemarks);

      setOmApproved(true);

      setData((prev) =>
        prev
          ? {
              ...prev,
              status: 'Approved - Operation Manager',
            }
          : prev,
      );

      setShowOMApproveModal(false);
      setOmRemarks('');

      setSuccessMessage('Outage Requisition approved successfully.');
    } catch (error) {
      console.error('Failed to approve outage request:', error);
      alert('Failed to approve outage request');
    } finally {
      setOmApproveLoading(false);
    }
  };
  const handleShutdownApprove = async () => {
    try {
      setShutdownLoading(true);

      await markShutdown(id!);

      setShutdownConfirmed(true);

      setData((prev) =>
        prev
          ? {
              ...prev,
              status: 'Shutdown Confirmed',
            }
          : prev,
      );

      setShowShutdownConfirmModal(false);

      setSuccessMessage('Shutdown confirmed successfully.');
    } catch (error) {
      console.error('Failed to confirm shutdown:', error);
      alert('Failed to confirm shutdown');
    } finally {
      setShutdownLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (downloadLoading) return;

    try {
      setDownloadLoading(true);
      const pdfBlob = await downloadOutageRequisitionPdf(id!);

      const url = window.URL.createObjectURL(new Blob([pdfBlob], { type: 'application/pdf' }));

      const link = document.createElement('a');

      link.href = url;

      link.setAttribute('download', `${data?.id || 'outage-requisition'}.pdf`);

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Download PDF failed:', error);

      const response = error?.response?.data;

      let message = 'Failed to download PDF.';

      if (response?.errors) {
        const firstKey = Object.keys(response.errors)[0];

        if (firstKey && response.errors[firstKey]?.length) {
          message = response.errors[firstKey][0];
        }
      } else if (response?.message) {
        message = response.message;
      }

      setErrorMessage({
        title: 'Download Failed',
        message,
      });

      setErrorModal(true);
    } finally {
      setDownloadLoading(false);
    }
  };
  const formatScheduleWindow = (startDate?: string | null, startTime?: string | null, endDate?: string | null, endTime?: string | null) => {
    const fmtDate = (iso?: string | null) => {
      if (!iso) return '-';
      const d = new Date(iso);
      return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('en-GB'); // DD/MM/YYYY
    };

    const fmtTime = (time?: string | null) => {
      if (!time) return '-';
      // Handles "HH:mm:ss" or "HH:mm" - just trim to HH:mm
      const match = time.match(/^(\d{2}):(\d{2})/);
      return match ? `${match[1]}:${match[2]}` : time;
    };

    return `${fmtDate(startDate)}, ${fmtTime(startTime)} to ${fmtDate(endDate)}, ${fmtTime(endTime)}`;
  };

  if (loading) return <Loader />;
  if (!data) return <div className="text-center py-10">No data found</div>;

  return (
    <>
      <div className="w-full bg-white relative">
        <div className="absolute -right-28 top-0">
          {!notified && profile?.role === 'planner' && data.createdBy == currentUserId && (
            <button onClick={handleShutdownClick} className="bg-red-600 text-white px-6 py-2 rounded-md font-medium hover:bg-red-700">
              Notify Senior Planner
            </button>
          )}

          {!outageConfirmed &&
            data.status?.startsWith('Outage Type Confirmation Pending - Senior Planner') &&
            confirmSrIds.includes(currentUserId) && (
              <div className="flex items-center gap-3">
                <RejectButton title="Outage Requisition" id={id!} apiPath="outage-requisitions" onSuccess={fetchData} />

                <button
                  onClick={() => setShowConfirmOutageModal(true)}
                  className="bg-red-600 text-white px-6 py-2 rounded-md font-medium hover:bg-red-700"
                >
                  Confirm Outage Type
                </button>
              </div>
            )}

          {!tsoNotified &&
            profile?.role === 'planner' &&
            data.createdBy == currentUserId &&
            data.status === 'Outage Type Confirmed - Senior Planner' &&
            data.createdBy == currentUserId && (
              <button
                onClick={handleNotifyTSOClick}
                className="bg-red-600 text-white px-6 py-2 rounded-md font-medium hover:bg-red-700 mt-2"
              >
                Notify Technical Support Officer
              </button>
            )}

          {!tsoConfirmed &&
            profile?.role === 'technical_support_officer' &&
            data.status?.startsWith('Outage Type Confirmation Pending - Technical Support Officer') &&
            confirmTsoIds.includes(currentUserId) && (
              <div className="flex items-center gap-3">
                <RejectButton title="Outage Requisition" id={id!} apiPath="outage-requisitions" onSuccess={fetchData} />

                <button
                  onClick={() => setShowTSOConfirmModal(true)}
                  className="bg-red-600 text-white px-6 py-2 rounded-md font-medium hover:bg-red-700"
                >
                  Confirm Outage Type
                </button>
              </div>
            )}

          {!mmNotified &&
            profile?.role === 'planner' &&
            data.status === 'Outage Type Confirmed - Technical Support Officer' &&
            data.createdBy == currentUserId && (
              <button
                onClick={handleNotifyMMClick}
                className="bg-red-600 text-white px-6 py-2 rounded-md font-medium hover:bg-red-700 mt-2"
              >
                Notify Maintenance Manager
              </button>
            )}

          {!mmApproved &&
            profile?.role === 'maintenance_manager' &&
            data.status?.startsWith('Approval Pending - Maintenance Manager') &&
            confirmMMIds.includes(currentUserId) && (
              <div className="flex items-center gap-3">
                <RejectButton title="Outage Requisition" id={id!} apiPath="outage-requisitions" onSuccess={fetchData} />

                <button
                  onClick={() => setShowMMApproveModal(true)}
                  className="bg-red-600 text-white px-6 py-2 rounded-md font-medium hover:bg-red-700 "
                >
                  Approve OR
                </button>
              </div>
            )}

          {!tmNotified &&
            profile?.role === 'planner' &&
            data.createdBy == currentUserId &&
            data.status === 'Approved - Maintenance Manager' &&
            data.createdBy == currentUserId && (
              <button
                onClick={handleNotifyOMClick}
                className="bg-red-600 text-white px-6 py-2 rounded-md font-medium hover:bg-red-700 mt-2"
              >
                Notify Technical Manager
              </button>
            )}

          {!tmApproved &&
            profile?.role === 'technical_manager' &&
            data.status?.startsWith('Approval Pending - Technical Manager') &&
            confirmTMIds.includes(currentUserId) && (
              <div className="flex items-center gap-3">
                <RejectButton title="Outage Requisition" id={id!} apiPath="outage-requisitions" onSuccess={fetchData} />

                <button
                  onClick={() => setShowTMApproveModal(true)}
                  className="bg-red-600 text-white px-6 py-2 rounded-md font-medium hover:bg-red-700"
                >
                  Approve OR
                </button>
              </div>
            )}

          {!omNotified &&
            profile?.role === 'planner' &&
            data.status === 'Approved - Technical Manager' &&
            data.createdBy == currentUserId && (
              <button
                onClick={handleNotifyOperationManagerClick}
                className="bg-red-600 text-white px-6 py-2 rounded-md font-medium hover:bg-red-700 mt-2"
              >
                Notify Operation Manager
              </button>
            )}

          {!omApproved &&
            profile?.role === 'operation_manager' &&
            data.status?.startsWith('Approval Pending - Operation Manager') &&
            confirmOMIds.includes(currentUserId) && (
              <div className="flex items-center gap-3">
                <RejectButton title="Outage Requisition" id={id!} apiPath="outage-requisitions" onSuccess={fetchData} />

                <button
                  onClick={() => setShowOMApproveModal(true)}
                  className="bg-red-600 text-white px-6 py-2 rounded-md font-medium hover:bg-red-700"
                >
                  Approve OR
                </button>
              </div>
            )}

          {!shiftSupervisorNotified &&
            profile?.role === 'planner' &&
            data.status === 'Approved - Operation Manager' &&
            data.createdBy == currentUserId && (
              <button
                onClick={handleNotifyShiftSupervisorClick}
                className="bg-red-600 text-white px-6 py-2 rounded-md font-medium hover:bg-red-700 mt-2"
              >
                Notify Shift Supervisor
              </button>
            )}

          {!shutdownConfirmed &&
            profile?.role === 'shift_supervisor' &&
            data.status?.startsWith('Shutdown Pending') &&
            confirmSSIds.includes(currentUserId) && (
              <div className="flex items-center gap-3">
                <RejectButton title="Outage Requisition" id={id!} apiPath="outage-requisitions" onSuccess={fetchData} />

                <button
                  onClick={() => setShowShutdownConfirmModal(true)}
                  className="bg-red-600 text-white px-6 py-2 rounded-md font-medium hover:bg-red-700"
                >
                  Confirm Shutdown
                </button>
              </div>
            )}

          {shutdownConfirmed && profile?.role === 'planner' && data.createdBy == currentUserId && (
            <button
              onClick={handleDownloadPdf}
              disabled={downloadLoading}
              className="bg-red-600 text-white px-6 py-2 rounded-md font-medium hover:bg-red-700 mt-2 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              {downloadLoading && (
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              )}
              {downloadLoading ? 'Downloading...' : 'Download'}
            </button>
          )}
        </div>
      </div>

      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-bold">
          {data.referenceNo} <span className="text-orange-500 text-sm font-medium">● {data.status}</span>
        </h2>
        <div className="flex items-center text-gray-500 mt-1 text-sm">
          <Calendar className="w-4 h-4 mr-1" />
          {formatScheduleWindow(data?.outageStartDate, data?.startTime, data?.outageEndDate, data?.endTime)}
        </div>
      </div>

      {/* Requisition summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-8 mb-8">
        <div>
          <h3 className="text-gray-500 text-sm mb-2">Name of Person Requiring Outage</h3>
          <div className="flex items-center">
            <User className="w-5 h-5 mr-2 text-gray-400" />
            <span className="font-medium text-gray-900">{data.requester}</span>
          </div>
        </div>

        <div>
          <h3 className="text-gray-500 text-sm mb-2">Site</h3>
          <div className="flex items-center">
            <MapPin className="w-5 h-5 mr-2 text-gray-400" />
            <span className="font-medium text-gray-900">{data.site}</span>
          </div>
        </div>

        <div>
          <h3 className="text-gray-500 text-sm mb-2">Unit Requested</h3>
          <div className="flex items-center">
            <Zap className="w-5 h-5 mr-2 text-gray-400" />
            <span className="font-medium text-gray-900">{data.unit}</span>
          </div>
        </div>

        <div>
          <h3 className="text-gray-500 text-sm mb-2">Date of Requisition</h3>
          <p className="font-medium text-gray-900">{data.requestDate ? new Date(data.requestDate).toLocaleDateString('en-GB') : '-'}</p>
        </div>

        <div>
          <h3 className="text-gray-500 text-sm mb-2">Outage Type</h3>
          <p className="font-medium text-gray-900">{data.outageType}</p>
        </div>

        <div>
          <h3 className="text-gray-500 text-sm mb-2">Outage Duration</h3>
          <p className="font-medium text-gray-900">{data.outageDuration || '-'}</p>
        </div>
      </div>

      {/* Reasons */}
      <div className="mb-6">
        <h3 className="text-gray-500 text-sm mb-3">Reason for Outage</h3>
        {data.reasons.length ? (
          data.reasons.map((reason, idx) => (
            <div
              key={idx}
              className="text-gray-900 prose max-w-[750px] w-full overflow-x-hidden break-words
          [&_ul]:list-disc
          [&_ul]:ml-6
          [&_ol]:list-decimal
          [&_ol]:ml-6
          [&_li]:mb-1
          [&_img]:max-w-full
          [&_img]:h-auto
          [&_table]:block
          [&_table]:overflow-x-auto
          [&_pre]:whitespace-pre-wrap
          [&_pre]:break-words"
              dangerouslySetInnerHTML={{
                __html: reason.description,
              }}
            />
          ))
        ) : (
          <p className="text-gray-900">No reasons provided</p>
        )}
      </div>

      <div className="mb-6">
        <h3 className="text-gray-500 text-sm mb-3">Isolation</h3>
        {data.isolations ? (
          <div
            className="text-gray-900 prose max-w-none break-words [&_ul]:list-disc [&_ul]:ml-6 [&_ol]:list-decimal [&_ol]:ml-6 [&_li]:mb-1"
            dangerouslySetInnerHTML={{ __html: data.isolations }}
          />
        ) : (
          <p className="text-gray-900">-</p>
        )}
      </div>

      <div className="mb-6">
        <h3 className="text-gray-500 text-sm mb-3">System Constraints</h3>
        {data.systemConstraints ? (
          <div
            className="text-gray-900 prose max-w-none break-words [&_ul]:list-disc [&_ul]:ml-6 [&_ol]:list-decimal [&_ol]:ml-6 [&_li]:mb-1"
            dangerouslySetInnerHTML={{ __html: data.systemConstraints }}
          />
        ) : (
          <p className="text-gray-900">-</p>
        )}
      </div>

      <div className="mb-6">
        <h3 className="text-gray-500 text-sm mb-3">Remarks on Previous Unit Outage</h3>
        {data.previousOutageRemarks ? (
          <div
            className="text-gray-900 prose max-w-none break-words [&_ul]:list-disc [&_ul]:ml-6 [&_ol]:list-decimal [&_ol]:ml-6 [&_li]:mb-1"
            dangerouslySetInnerHTML={{ __html: data.previousOutageRemarks }}
          />
        ) : (
          <p className="text-gray-900">-</p>
        )}
      </div>

      {isShutdownModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-lg w-[500px] p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Notify a Senior Planner to confirm the outage type</h3>

              <button
                onClick={() => {
                  setIsShutdownModalOpen(false);
                  setSelectedPlanner('');
                }}
                disabled={notifyLoading}
                className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
              >
                ✕
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Senior Planner</label>

              {/* <select
                value={selectedPlanner}
                onChange={(e) => setSelectedPlanner(e.target.value)}
                disabled={notifyLoading}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="">Select Planner</option>

                {seniorPlanners.map((planner) => (
                  <option key={planner.id} value={planner.id}>
                    {planner.name}
                  </option>
                ))}
              </select> */}
              <SearchableSelect
                options={seniorPlanners}
                value={selectedPlanner}
                onChange={setSelectedPlanner}
                placeholder="Search Senior Planner"
                disabled={notifyLoading}
              />
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setIsShutdownModalOpen(false);
                  setSelectedPlanner('');
                }}
                disabled={notifyLoading}
                className="px-4 py-2 border rounded-md disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                onClick={handleShutdownConfirm}
                disabled={notifyLoading}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {notifyLoading ? 'Notifying...' : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="fixed bottom-5 right-5 bg-green-500 text-white px-5 py-3 rounded-lg shadow-lg z-50">
          <div className="font-semibold">Success</div>

          <div className="text-sm">{successMessage}</div>
        </div>
      )}

      {showConfirmOutageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl w-[400px] p-6 shadow-2xl text-center">
            <h3 className="text-lg font-semibold mb-6">Are you sure you want to confirm the Outage Type?</h3>

            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowConfirmOutageModal(false)}
                className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>

              <button
                onClick={handleConfirmOutageType}
                disabled={confirmLoading}
                className="px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {confirmLoading ? 'Confirming...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showTSOModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-lg w-[500px] p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Notify Technical Support Officer to confirm outage type</h3>

              <button
                onClick={() => {
                  setShowTSOModal(false);
                  setSelectedTSO('');
                }}
                // className="text-gray-500 hover:text-gray-700"
                disabled={notifyLoading}
                className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
              >
                ✕
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Technical Support Officer</label>

              {/* <select
                value={selectedTSO}
                onChange={(e) => setSelectedTSO(e.target.value)}
                disabled={notifyLoading}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="">Select Technical Support Officer</option>

                {technicalSupportOfficers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select> */}
              <SearchableSelect
                options={technicalSupportOfficers}
                value={selectedTSO}
                onChange={setSelectedTSO}
                placeholder="Search Technical Support Officer "
                disabled={notifyLoading}
              />
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowTSOModal(false);
                  setSelectedTSO('');
                }}
                disabled={notifyLoading}
                className="px-4 py-2 border rounded-md disabled:opacity-50"
              >
                Cancel
              </button>

              {/* <button onClick={handleTSOConfirm} className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700">
                Done
              </button> */}
              <button
                onClick={handleTSOConfirm}
                disabled={notifyLoading}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {notifyLoading ? 'Notifying...' : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showTSOConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl w-[400px] p-6 shadow-2xl text-center">
            <h3 className="text-lg font-semibold mb-6">Are you sure you want to confirm the outage type?</h3>

            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowTSOConfirmModal(false)}
                className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>

              <button
                onClick={handleTSOOutageConfirm}
                disabled={tsoConfirmLoading}
                className="px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {tsoConfirmLoading ? 'Confirming...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showMMModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-lg w-[500px] p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Notify Maintenance Manager to approve outage request</h3>

              <button
                onClick={() => {
                  setShowMMModal(false);
                  setSelectedMM('');
                }}
                // className="text-gray-500 hover:text-gray-700"
                disabled={notifyLoading}
                className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
              >
                ✕
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Maintenance Manager</label>

              {/* <select
                value={selectedMM}
                onChange={(e) => setSelectedMM(e.target.value)}
                disabled={notifyLoading}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="">Select Maintenance Manager</option>

                {maintenanceManagers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select> */}
              <SearchableSelect
                options={maintenanceManagers}
                value={selectedMM}
                onChange={setSelectedMM}
                placeholder="Search Maintenance Manager "
                disabled={notifyLoading}
              />
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowMMModal(false);
                  setSelectedMM('');
                }}
                // className="px-4 py-2 border rounded-md"
                disabled={notifyLoading}
                className="px-4 py-2 border rounded-md disabled:opacity-50"
              >
                Cancel
              </button>

              {/* <button onClick={handleMMConfirm} className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700">
                Done
              </button> */}
              <button
                onClick={handleMMConfirm}
                disabled={notifyLoading}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {notifyLoading ? 'Notifying...' : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showMMApproveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl w-[400px] p-6 shadow-2xl text-center">
            <h3 className="text-lg font-semibold mb-6">Are you sure you want to approve the outage request?</h3>

            <textarea
              value={mmRemarks}
              onChange={(e) => setMmRemarks(e.target.value)}
              rows={4}
              className="w-full border border-gray-300 rounded-md px-3 py-2 mb-4"
              placeholder="Enter remarks (optional)"
            />

            <div className="flex justify-center gap-4">
              <button
                onClick={() => {
                  setShowMMApproveModal(false);
                  setMmRemarks('');
                }}
                className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>

              <button
                onClick={handleMMApprove}
                disabled={mmApproveLoading}
                className="px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {mmApproveLoading ? 'Approving...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showTMModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-lg w-[500px] p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Notify Technical Manager to approve outage request</h3>

              <button
                onClick={() => {
                  setShowTMModal(false);
                  setSelectedTM('');
                }}
                // className="text-gray-500 hover:text-gray-700"
                disabled={notifyLoading}
                className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
              >
                ✕
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Technical Manager</label>

              {/* <select
                value={selectedTM}
                onChange={(e) => setSelectedTM(e.target.value)}
                disabled={notifyLoading}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="">Select Technical Manager</option>

                {technicalManagers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select> */}
              <SearchableSelect
                options={technicalManagers}
                value={selectedTM}
                onChange={setSelectedTM}
                placeholder="Search Technical Manager "
                disabled={notifyLoading}
              />
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowTMModal(false);
                  setSelectedTM('');
                }}
                // className="px-4 py-2 border rounded-md"
                disabled={notifyLoading}
                className="px-4 py-2 border rounded-md disabled:opacity-50"
              >
                Cancel
              </button>

              {/* <button onClick={handleOMConfirm} className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700">
                Done
              </button> */}
              <button
                onClick={handleOMConfirm}
                disabled={notifyLoading}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {notifyLoading ? 'Notifying...' : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showTMApproveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl w-[400px] p-6 shadow-2xl text-center">
            <h3 className="text-lg font-semibold mb-6">Are you sure you want to approve the outage request?</h3>

            <textarea
              value={tmRemarks}
              onChange={(e) => setTmRemarks(e.target.value)}
              rows={4}
              className="w-full border border-gray-300 rounded-md px-3 py-2 mb-4"
              placeholder="Enter remarks (optional)"
            />

            <div className="flex justify-center gap-4">
              <button
                onClick={() => {
                  setShowTMApproveModal(false);
                  setTmRemarks('');
                }}
                className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>

              <button
                onClick={handleTMApprove}
                disabled={tmApproveLoading}
                className="px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {tmApproveLoading ? 'Approving...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showOMModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-lg w-[500px] p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Notify Operation Manager to approve outage request</h3>

              <button
                onClick={() => {
                  setShowOMModal(false);
                  setSelectedOM('');
                }}
                // className="text-gray-500 hover:text-gray-700"
                disabled={notifyLoading}
                className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
              >
                ✕
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Operation Manager</label>

              {/* <select
                value={selectedOM}
                onChange={(e) => setSelectedOM(e.target.value)}
                disabled={notifyLoading}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="">Select Operation Manager</option>

                {operationManagers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select> */}
              <SearchableSelect
                options={operationManagers}
                value={selectedOM}
                onChange={setSelectedOM}
                placeholder="Search Operation Manager "
                disabled={notifyLoading}
              />
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowOMModal(false);
                  setSelectedOM('');
                }}
                // className="px-4 py-2 border rounded-md"
                disabled={notifyLoading}
                className="px-4 py-2 border rounded-md disabled:opacity-50"
              >
                Cancel
              </button>

              {/* <button onClick={handleOperationManagerConfirm} className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700">
                Done
              </button> */}
              <button
                onClick={handleOperationManagerConfirm}
                disabled={notifyLoading}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {notifyLoading ? 'Notifying...' : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showOMApproveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl w-[400px] p-6 shadow-2xl text-center">
            <h3 className="text-lg font-semibold mb-6">Are you sure you want to approve the outage request?</h3>

            <textarea
              value={omRemarks}
              onChange={(e) => setOmRemarks(e.target.value)}
              rows={4}
              className="w-full border border-gray-300 rounded-md px-3 py-2 mb-4"
              placeholder="Enter remarks (optional)"
            />

            <div className="flex justify-center gap-4">
              <button
                onClick={() => {
                  setShowOMApproveModal(false);
                  setOmRemarks('');
                }}
                className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>

              <button
                onClick={handleOMApprove}
                disabled={omApproveLoading}
                className="px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {omApproveLoading ? 'Approving...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showShiftSupervisorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-lg w-[500px] p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Notify Shift Supervisor for Shutdown</h3>

              <button
                onClick={() => {
                  setShowShiftSupervisorModal(false);
                  setSelectedShiftSupervisor('');
                }}
                // className="text-gray-500 hover:text-gray-700"
                disabled={notifyLoading}
                className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
              >
                ✕
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Shift Supervisor</label>

              {/* <select
                value={selectedShiftSupervisor}
                onChange={(e) => setSelectedShiftSupervisor(e.target.value)}
                disabled={notifyLoading}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="">Select Shift Supervisor</option>

                {shiftSupervisors.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select> */}
              <SearchableSelect
                options={shiftSupervisors}
                value={selectedShiftSupervisor}
                onChange={setSelectedShiftSupervisor}
                placeholder="Search Technical Manager "
                disabled={notifyLoading}
              />
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowShiftSupervisorModal(false);
                  setSelectedShiftSupervisor('');
                }}
                // className="px-4 py-2 border rounded-md"
                disabled={notifyLoading}
                className="px-4 py-2 border rounded-md disabled:opacity-50"
              >
                Cancel
              </button>

              {/* <button onClick={handleShiftSupervisorConfirm} className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700">
                Done
              </button> */}
              <button
                onClick={handleShiftSupervisorConfirm}
                disabled={notifyLoading}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {notifyLoading ? 'Notifying...' : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showShutdownConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl w-[400px] p-6 shadow-2xl text-center">
            <h3 className="text-lg font-semibold mb-6">Are you sure you want to confirm the shutdown?</h3>

            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowShutdownConfirmModal(false)}
                className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>

              <button
                onClick={handleShutdownApprove}
                disabled={shutdownLoading}
                className="px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {shutdownLoading ? 'Confirming...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ErrorModal
        isOpen={errorModal}
        onClose={() => setErrorModal(false)}
        title={errorMessage.title}
        message={errorMessage.message}
        buttonText="OK"
        onButtonClick={() => setErrorModal(false)}
      />
    </>
  );
};

export default OutageReqDetailsPage;
